import React from "react";
import { MenuList, Popover } from "@mui/material";

export const useContextMenu = <T extends HTMLElement>(
  menu?: React.ReactNode
): readonly [
  (e: React.MouseEvent<T>) => void,
  JSX.Element | null,
  () => void,
] => {
  const [element, setElement] = React.useState<Element | null>(null);

  const handleClick = React.useCallback(
    (e: React.MouseEvent<T>) => {
      if (!menu) return;
      e.preventDefault();
      e.stopPropagation();
      const element = e.currentTarget || e.target;
      if (element && element instanceof Element) {
        setElement(element);
      }
    },
    [menu]
  );

  const handleClose = React.useCallback(() => {
    setElement(null);
  }, []);

  const menuElement = React.useMemo(
    () =>
      element ? (
        <Popover
          disableAutoFocus={true}
          disableEnforceFocus={true}
          disableRestoreFocus={true}
          disableScrollLock={true}
          open={true}
          anchorEl={element}
          onClose={(e, reason) => {
            if (reason === "escapeKeyDown") return;
            (e as any)?.preventDefault();
            (e as any)?.stopPropagation();
            handleClose();
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          slotProps={{
            paper: {
              onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
              },
              onTouchStart: (e: React.TouchEvent<HTMLDivElement>) => {
                e.stopPropagation();
              },
              onClick: (e: React.MouseEvent<HTMLDivElement>) => {
                e.preventDefault();
                e.stopPropagation();
                setElement(null);
              },
            },
          }}
        >
          <MenuList disablePadding>{menu}</MenuList>
        </Popover>
      ) : null,
    [element, menu, handleClose]
  );

  return [handleClick, menuElement, handleClose] as const;
};
